/**
 * File embed — Phase 4c inline atomic embed (wiki feature plan §4c).
 *
 * Renders as a `<span data-block-type="file-embed">` chip carrying
 * file-type icon + name + size + download button. Inline atomic so the
 * chip sits next to surrounding prose; click on the download icon opens
 * a fresh presigned GET URL in a new tab.
 *
 * Schema shape — inline atomic, mirrors status / date / work-item:
 *   - `group: 'inline'` + `inline: true` + `atom: true`
 *   - `selectable: true`, `draggable: false`, `marks: ''`
 *
 * Attributes:
 *   - `fileId: string | null`     — attachment row UUID.
 *   - `fileName: string`          — original filename (e.g. "spec.pdf").
 *   - `sizeBytes: number | null`  — file size for the chip's size column.
 *   - `mimeType: string | null`   — drives file-type icon selection.
 *   - `uploadingPlaceholderId` (transient) — client-side id the OS-drop
 *     upload flow uses to locate this node once the upload settles
 *     (mirrors the card `fileEmbed`'s attr of the same name). Never
 *     round-trips through HTML.
 *
 * Round-trip:
 *   - parseHTML matches `span[data-block-type="file-embed"]`.
 *   - renderHTML emits data-* attrs (omitted when null/empty).
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

export interface BuildFileEmbedExtensionOptions {
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildFileEmbedExtension({ view }: BuildFileEmbedExtensionOptions) {
  return Node.create({
    name: 'fileEmbed',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    marks: '',

    addAttributes() {
      return {
        fileId: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-file-id')
            return isUuid(raw) ? raw : null
          },
          renderHTML: (attrs: { fileId: string | null }) =>
            attrs.fileId ? { 'data-file-id': attrs.fileId } : {},
        },
        fileName: {
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-file-name') ?? '',
          renderHTML: (attrs: { fileName: string }) =>
            attrs.fileName ? { 'data-file-name': attrs.fileName } : {},
        },
        sizeBytes: {
          default: null as number | null,
          parseHTML: (el: HTMLElement) => {
            const raw = el.getAttribute('data-size-bytes')
            if (raw === null) return null
            const n = Number.parseInt(raw, 10)
            return Number.isFinite(n) && n >= 0 ? n : null
          },
          renderHTML: (attrs: { sizeBytes: number | null }) =>
            attrs.sizeBytes !== null && attrs.sizeBytes >= 0
              ? { 'data-size-bytes': String(attrs.sizeBytes) }
              : {},
        },
        mimeType: {
          default: null as string | null,
          parseHTML: (el: HTMLElement) => el.getAttribute('data-mime-type'),
          renderHTML: (attrs: { mimeType: string | null }) =>
            attrs.mimeType ? { 'data-mime-type': attrs.mimeType } : {},
        },
        uploadingPlaceholderId: {
          // In-memory-only — the OS-drop upload flow uses it to locate
          // the node while the upload is in flight.
          default: null as string | null,
          parseHTML: () => null,
          renderHTML: () => ({}),
        },
        uploadingFailed: {
          // In-memory-only — flips the uploading placeholder into the
          // error branch (retry / dismiss). Never serialized; the
          // flush-time strip removes the node via uploadingPlaceholderId.
          default: false,
          parseHTML: () => false,
          renderHTML: () => ({}),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'span[data-block-type="file-embed"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, 'data-block-type': 'file-embed' }]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },
  })
}

export { isUuid as isFileUuid }
